import { Tabs } from "antd"
import { useEffect, useState } from "react"
import { connect, useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router"

import { actions as documentActions } from "client/documents/actions"
import { DocumentType } from "client/documents/types"
import { Card } from "components/Card/Card"
import Loader from "components/Loaders/Loader/Loader"
import { GenericModal } from "components/Modal/Modal"
import PreviousButton from "components/Previous/PreviousButton"
import { Subtitle } from "components/Title/Title"
import { Diagnosis } from "containers/Diagnosis/Diagnosis"
import { hasBothMediaPermissionsGranted } from "containers/Diagnosis/utils"
import { logout } from "containers/Login/services/actions"
import Newsletter from "containers/Newsletter"
import { History } from "containers/Profile/History/History"
import { AddCard, CardHistory } from "containers/Stripe"
import { Customer, Prospect, Relative } from "types/entity"
import { Dispatcher, Message } from "types/redux"
import { ClientStore, ProfileStore } from "types/store"

import { areConnectedDeviceOff } from "../Call/utils/index"
import { PatientInformation } from "./PatientInformation"
import { SectionHeader } from "./SectionHeader"
import { TreatingDoctor } from "./TreatingDoctor"
import styles from "./Profile.module.scss"
import actions from "./services/actions"
import dayjs from "dayjs"
import { ChangePasswordModal } from "components/Modal/ChangePasswordModal"
import { GET_CALL } from "containers/Call/services/constants"
import { useLanguage } from "locales"
import ChooseSpecialist from "containers/ChooseSpecialist/ChooseSpecialist"
import FatButton from "components/Button/FatButton/FatButton"
import { cdn } from "core/cdn"
import { PROFILE_DOCUMENTS_PAGE, PROFILE_INFORMATIONS_TAB } from "core/constants"

const checkMediaPermissions = () => {
  return hasBothMediaPermissionsGranted().then((granted) => {
    if (!granted) {
      throw new Error("Camera or microphone permission missing")
    }
    return granted
  })
}

const checkConnectedDeviceOff = () => {
  return areConnectedDeviceOff().then((res) => {
    if (!res) {
      throw new Error("Device still on")
    }
    return res
  })
}

type InfoSection = "menu" | "personal" | "treating-doctor"

// Tiles of the "Profil" tab
const INFO_TILE_STYLE: React.CSSProperties = {
  minHeight: 280,
  paddingBottom: 10,
  justifyContent: "flex-start",
  paddingTop: 40,
  gap: 20,
}
const INFO_TILE_IMG_STYLE: React.CSSProperties = {
  width: 100,
  height: 100,
  flexGrow: 0,
  flexShrink: 0,
}
const INFO_TILE_LABEL_STYLE: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 400,
  color: "#211452",
  textAlign: "center",
  flexGrow: 0,
  flexShrink: 0,
  height: 80,
  width: 270,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}

const Profile = (props: ProfileStore) => {
  const { languages } = useLanguage()
  // Sub-pages (Documents importés) come back here with the tab to reopen
  const location = useLocation()
  const navigate = useNavigate()
  const requestedTab = (location.state as { tab?: string } | null)?.tab
  const [logoutModalVisible, setLogoutModalVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [diagnosis, setDiagnosis] = useState(false)
  const [proceedAfterDiagnostic, setProceedAfterDiagnostic] = useState<(() => void) | null>(null)
  const [shouldResetPassword, setShouldResetPassword] = useState(false)
  const [infoSection, setInfoSection] = useState<InfoSection>("menu")
  const welcomeTitle = `${languages.welcome}, ${props.patient.firstname}`
  const dispatch = useDispatch()
  const [TLC, INFORMATIONS, HISTORY, PAIEMENT, OTHERS] = [
    "TLC",
    PROFILE_INFORMATIONS_TAB,
    "HISTORY",
    "PAIEMENT",
    "OTHERS",
  ]
  const [activeTabKey, setActiveTabKey] = useState(
    requestedTab === INFORMATIONS ? INFORMATIONS : TLC,
  )

  useEffect(() => {
    const now = dayjs()
    const daysDiff = (props.patient.password_expiration_date).diff(now, "days")
    if (daysDiff <= 7) {
      // Propose le changement de mot de passe
      setShouldResetPassword(true)
    }
  }, [props.patient.password_expiration_date])

  useEffect(() => {
    if (props.success) props.resetProfile()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.success])



  const startDiagnostic = (proceed: () => void) => {
    setLoading(true)
    setProceedAfterDiagnostic(() => proceed)
    checkMediaPermissions()
      .then(checkConnectedDeviceOff)
      .then(() => {
        setDiagnosis(false)
        proceed()
      })
      .catch(() => setDiagnosis(true))
      .finally(() => {
        setLoading(false)
      })
  }

  if (shouldResetPassword)
    return <ChangePasswordModal
      patient={props.patient}
      onCancel={() => setShouldResetPassword(false)}
      onFinish={() => {
        setShouldResetPassword(false)
        dispatch({ type: GET_CALL })
      }} />

  if (diagnosis)
    return (
      <Diagnosis
        onStop={() => setDiagnosis(false)}
        onFinish={() => {
          setDiagnosis(false)
          proceedAfterDiagnostic?.()
        }}
      />
    )

  return (
    <Card subtitle={welcomeTitle} message={props.message}>
      <PreviousButton
        danger
        text={languages.logout}
        onClick={() => setLogoutModalVisible(true)}
      />
      <Tabs
        className={styles.ProfileTabs}
        activeKey={activeTabKey}
        onChange={(key: string) => {
          if (key === HISTORY && !props.calls) props.getHistory()
          setActiveTabKey(key)
        }}
      >
        {/* TLC */}
        <Tabs.TabPane
          className={styles.Scroller}
          tab={<Subtitle text={languages.chooseAPath} />}
          key={TLC}
        >
          <div style={{ width: '100%', height: '100%' }}>
            <ChooseSpecialist startDiagnostic={startDiagnostic} loading={loading} />
          </div>
        </Tabs.TabPane>
        {/* INFORMATIONS */}
        <Tabs.TabPane
          className={styles.Scroller}
          tab={<Subtitle text={languages.profile_myInformations} />}
          key={INFORMATIONS}
        >
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 60, marginTop: 50 }}>
            <FatButton
              img={cdn("images/pico_piece_administrative.svg")}
              imgStyle={INFO_TILE_IMG_STYLE}
              style={INFO_TILE_STYLE}
              onClick={() => setInfoSection("personal")}
            >
              <span style={INFO_TILE_LABEL_STYLE}>
                {languages.personalInformationsSubtitle}
              </span>
            </FatButton>
            <FatButton
              img={cdn("/images/ps_picto.svg")}
              imgStyle={INFO_TILE_IMG_STYLE}
              style={INFO_TILE_STYLE}
              onClick={() => setInfoSection("treating-doctor")}
            >
              <span style={INFO_TILE_LABEL_STYLE}>
                {languages.treatingDoctor}
              </span>
            </FatButton>
            <FatButton
              img={cdn("images/pico_compte-rendu.svg")}
              imgStyle={INFO_TILE_IMG_STYLE}
              style={INFO_TILE_STYLE}
              onClick={() => navigate(PROFILE_DOCUMENTS_PAGE)}
            >
              <span style={INFO_TILE_LABEL_STYLE}>
                {languages.importedDocuments}
              </span>
            </FatButton>
          </div>
        </Tabs.TabPane>
        {/* HISTORY */}
        <Tabs.TabPane
          className={styles.Scroller}
          tab={<Subtitle text={languages.profile_myHistory} />}
          key={HISTORY}
        >
          {props.calls && <History calls={props.calls} />}
          {props.loading && <Loader />}
        </Tabs.TabPane>

        {/* PAIEMENT */}
        <Tabs.TabPane
          className={styles.Scroller}
          tab={<Subtitle text={languages.profile_myPayments} />}
          key={PAIEMENT}
        >
          <CardHistory />
          <AddCard />
        </Tabs.TabPane>
        <Tabs.TabPane
          className={styles.Scroller}
          tab={<Subtitle text={languages.profile_mySettings} />}
          key={OTHERS}
        >
          <Newsletter onNavigateToInformations={() => setActiveTabKey(INFORMATIONS)} active={activeTabKey === OTHERS} />
        </Tabs.TabPane>
      </Tabs>
      <GenericModal
        visible={logoutModalVisible}
        title={languages.logoutTitle}
        acceptText={languages.logout}
        onCancel={() => setLogoutModalVisible(false)}
        onAccept={() => {
          props.logout()
        }}
      />
      <GenericModal visible={infoSection === "personal"} noFooter style={{ width: 1650 }} bodyStyle={{ height: 900, overflowY: "auto" }}>
        <div>
          <SectionHeader onBack={() => setInfoSection("menu")} title={languages.personalInformationsSubtitle} />
          <PatientInformation
            loading={props.loading}
            update_requires_action={props.update_requires_action}
            patient={props.patient}
            relatives={props.relatives}
            change={props.change}
            changeRelative={props.changeRelative}
          />
        </div>
      </GenericModal>
      <GenericModal visible={infoSection === "treating-doctor"} noFooter style={{ width: 1650 }} bodyStyle={{ height: 900, overflowY: "auto" }}>
        <TreatingDoctor
          loading={props.loading}
          patient={props.patient}
          change={props.change}
          onBack={() => setInfoSection("menu")}
        />
      </GenericModal>
    </Card>
  )
}

const mapStateToProps = ({
  profile,
  client,
  stripe,
}: {
  profile: ProfileStore
  client: ClientStore
  stripe: { message?: Message }
}): ProfileStore => {
  const stripeMessage = stripe.message
  return {
    ...profile,
    patient: client.customer as Customer,
    relatives: client.relatives as Relative[],
    message: stripeMessage || profile.message,
  }
}
const mapDispatchToProps = (dispatch: Dispatcher): Partial<ProfileStore> => ({
  change: (p: Partial<Prospect>) => dispatch(actions.changePatientRequest(p)),
  changeRelative: (r: Partial<Relative>) =>
    dispatch(actions.changeRelativeRequest(r)),
  getHistory: () => dispatch(actions.historyRequest()),
  logout: () => dispatch(logout()),
  getDocuments: (id: string, d?: DocumentType[]) =>
    dispatch(documentActions.request(id, d)),
  resetProfile: () => dispatch(actions.reset()),
})

export default connect(mapStateToProps, mapDispatchToProps)(Profile)
